import { useState, useEffect } from 'react';
import { updateCommonSetting } from '../../../../services/settingService';

interface GeneralSettingsProps {
  settings: any;
  onUpdate: (settings: any) => void;
}

const GeneralSettings = ({ settings, onUpdate }: GeneralSettingsProps) => {
  const [formData, setFormData] = useState({
    siteName: '',
    tagline: '',
    description: '',
    emails: [] as Array<{ label: string; address: string }>,
    phone: '',
    address: '',
    timezone: 'Asia/Ho_Chi_Minh',
    defaultLanguage: 'vi'
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (settings) {
      setFormData({
        siteName: settings.siteName || '',
        tagline: settings.tagline || '',
        description: settings.description || '',
        emails: settings.emails || [],
        phone: settings.phone || '',
        address: settings.address || '',
        timezone: settings.timezone || 'Asia/Ho_Chi_Minh',
        defaultLanguage: settings.defaultLanguage || 'vi'
      });
    }
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await updateCommonSetting(formData);
      if (response.success) {
        setMessage({ type: 'success', text: response.message || 'Cập nhật thành công' });
        if (onUpdate && response.data) {
          onUpdate(response.data);
        }
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Có lỗi xảy ra khi cập nhật'
      });
    } finally {
      setLoading(false);
    }
  };

  const addEmail = () => {
    setFormData({
      ...formData,
      emails: [...formData.emails, { label: '', address: '' }]
    });
  };

  const removeEmail = (index: number) => {
    const newEmails = formData.emails.filter((_, i) => i !== index);
    setFormData({ ...formData, emails: newEmails });
  };

  const updateEmail = (index: number, field: string, value: string) => {
    const newEmails = [...formData.emails];
    newEmails[index] = { ...newEmails[index], [field]: value };
    setFormData({ ...formData, emails: newEmails });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-xl font-bold text-gray-900 mb-6">Cài đặt chung</h3>
      
      {message.text && (
        <div className={`mb-4 p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tên website
            </label>
            <input
              type="text"
              value={formData.siteName}
              onChange={(e) => setFormData({ ...formData, siteName: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tagline
            </label>
            <input
              type="text"
              value={formData.tagline}
              onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Mô tả website
          </label>
          <textarea
            rows={4}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Số điện thoại
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
          />
        </div>

        {/* Emails */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Email liên hệ
            </label>
            <button
              type="button"
              onClick={addEmail}
              className="px-4 py-2 bg-[#2A4B7C] hover:bg-[#2A4B7C]/90 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <i className="ri-add-line mr-1"></i>
              Thêm email
            </button>
          </div>

          {formData.emails.map((email, index) => (
            <div key={index} className="flex gap-2 mb-2">
              <input
                type="text"
                value={email.label || ''}
                onChange={(e) => updateEmail(index, 'label', e.target.value)}
                placeholder="Nhãn (ví dụ: Liên hệ, Support)"
                className="w-1/3 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
              />
              <input
                type="email"
                value={email.address || ''}
                onChange={(e) => updateEmail(index, 'address', e.target.value)}
                placeholder="email@example.com"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => removeEmail(index)}
                className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </div>
          ))}

          {formData.emails.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">
              Chưa có email nào. Nhấn "Thêm email" để bắt đầu.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Địa chỉ
          </label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Múi giờ
            </label>
            <select
              value={formData.timezone}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent pr-8"
            >
              <option value="Asia/Ho_Chi_Minh">Asia/Ho_Chi_Minh (UTC+7)</option>
              <option value="Asia/Bangkok">Asia/Bangkok (UTC+7)</option>
              <option value="Asia/Singapore">Asia/Singapore (UTC+8)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ngôn ngữ mặc định
            </label>
            <select
              value={formData.defaultLanguage}
              onChange={(e) => setFormData({ ...formData, defaultLanguage: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent pr-8"
            >
              <option value="vi">Tiếng Việt</option>
              <option value="en">English</option>
            </select>
          </div>
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className="bg-[#2A4B7C] hover:bg-[#2A4B7C]/90 disabled:opacity-50 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg font-semibold transition-colors"
          >
            {loading ? 'Đang lưu...' : 'Lưu thay đổi'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GeneralSettings;

